import { supabase } from './supabase';
import { UserService } from './userService';
import type { Database } from '../types/database.types';
import { logger } from '../utils/logger';

type Company = Database['public']['Tables']['companies']['Row'];
type Branch = Database['public']['Tables']['branches']['Row'];
type User = Database['public']['Tables']['users']['Row'];

export interface CompanyWithBranches extends Company {
  branches?: Branch[];
  userCount?: number;
  branchCount?: number;
}

export interface BranchWithUsers extends Branch {
  company?: Company;
  users?: User[];
  userCount?: number;
}

export interface UserWithRelations extends User {
  company?: Company;
  branch?: Branch;
}

export class OrganizationService {
  // 회사 목록 조회
  static async getCompanies() {
    try {
      const { data: companies, error } = await supabase
        .from('companies')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;

      // 각 회사의 지점 수와 사용자 수 계산
      const companiesWithStats = await Promise.all((companies || []).map(async (company) => {
        // 지점 수 조회
        const { count: branchCount } = await supabase
          .from('branches')
          .select('id', { count: 'exact' })
          .eq('company_id', company.id);

        // 사용자 수 조회
        const { count: userCount } = await supabase
          .from('users')
          .select('id', { count: 'exact' })
          .eq('company_id', company.id)
          .eq('is_active', true)
          .eq('is_deleted', false);

        // 지점 목록 조회
        const { data: branches } = await supabase
          .from('branches')
          .select('*')
          .eq('company_id', company.id)
          .order('branch_name', { ascending: true });

        return {
          ...company,
          branches: branches || [],
          branchCount: branchCount || 0,
          userCount: userCount || 0
        } as CompanyWithBranches;
      }));

      return companiesWithStats;
    } catch (error) {
      logger.error('Error fetching companies', error);
      throw error;
    }
  }

  // 회사 코드 자동 생성
  static async generateCompanyCode(): Promise<string> {
    try {
      // 모든 회사 목록 조회
      const { data: companies, error } = await supabase
        .from('companies')
        .select('company_code')
        .order('company_code', { ascending: false });

      if (error) throw error;

      // 가장 큰 번호 찾기
      let maxNumber = 0;
      if (companies && companies.length > 0) {
        companies.forEach(company => {
          if (company.company_code) {
            // COM 뒤의 모든 숫자를 추출
            const match = company.company_code.match(/COM(\d+)/);
            if (match) {
              const num = parseInt(match[1]);
              if (num > maxNumber) maxNumber = num;
            }
          }
        });
      }

      // 다음 번호로 코드 생성
      const nextNumber = maxNumber + 1;
      return `COM${String(nextNumber).padStart(3, '0')}`;
    } catch (error) {
      logger.error('Error generating company code', error);
      // 에러 시 타임스탬프 기반 코드 생성
      return `COM${Date.now().toString().slice(-4)}`;
    }
  }

  // 회사 생성
  static async createCompany(companyData: {
    name: string;
    company_code?: string;
  }) {
    try {
      // company_code가 없으면 자동 생성
      let companyCode = companyData.company_code;
      if (!companyCode) {
        companyCode = await this.generateCompanyCode();
      }

      const { data, error } = await supabase
        .from('companies')
        .insert({
          name: companyData.name,
          company_code: companyCode
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error creating company', error, { companyData });
      throw error;
    }
  }

  // 회사 수정
  static async updateCompany(companyId: string, companyData: {
    name?: string;
    company_code?: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('companies')
        .update(companyData)
        .eq('id', companyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error updating company', error, { id: companyId, companyData });
      throw error;
    }
  }

  // 회사 삭제
  static async deleteCompany(companyId: string) {
    try {
      // 회사에 속한 지점이나 사용자가 있는지 확인
      const { count: branchCount } = await supabase
        .from('branches')
        .select('id', { count: 'exact' })
        .eq('company_id', companyId);

      const { count: userCount } = await supabase
        .from('users')
        .select('id', { count: 'exact' })
        .eq('company_id', companyId)
        .eq('is_deleted', false);

      if ((branchCount || 0) > 0 || (userCount || 0) > 0) {
        throw new Error('회사에 속한 지점이나 사용자가 있어 삭제할 수 없습니다.');
      }

      const { error } = await supabase
        .from('companies')
        .delete()
        .eq('id', companyId);

      if (error) throw error;
    } catch (error) {
      logger.error('Error deleting company', error, { id: companyId });
      throw error;
    }
  }

  // 지점 목록 조회
  static async getBranches(companyId?: string) {
    try {
      let query = supabase
        .from('branches')
        .select(`
          *,
          company:companies(*)
        `)
        .order('branch_name', { ascending: true });

      if (companyId) {
        query = query.eq('company_id', companyId);
      }

      const { data: branches, error } = await query;

      if (error) throw error;

      // 각 지점의 사용자 수 계산
      const branchesWithStats = await Promise.all((branches || []).map(async (branch) => {
        const { count: userCount } = await supabase
          .from('users')
          .select('id', { count: 'exact' })
          .eq('branch_id', branch.id)
          .eq('is_active', true)
          .eq('is_deleted', false);

        return {
          ...branch,
          userCount: userCount || 0
        } as BranchWithUsers;
      }));

      return branchesWithStats;
    } catch (error) {
      logger.error('Error fetching branches', error, { companyId });
      throw error;
    }
  }

  // 지점 코드 자동 생성
  static async generateBranchCode(companyId: string): Promise<string> {
    try {
      // 먼저 회사 코드를 가져옴
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('company_code')
        .eq('id', companyId)
        .single();

      if (companyError) throw companyError;

      const companyCode = company.company_code || 'COM';

      // 해당 회사의 지점 목록 조회
      const { data: branches, error } = await supabase
        .from('branches')
        .select('branch_code')
        .eq('company_id', companyId)
        .order('branch_code', { ascending: false });

      if (error) throw error;

      // 가장 큰 번호 찾기
      let maxNumber = 0;
      if (branches && branches.length > 0) {
        branches.forEach(branch => {
          if (branch.branch_code) {
            // 회사코드-BR숫자 형식에서 숫자 추출
            const match = branch.branch_code.match(/BR(\d+)/);
            if (match) {
              const num = parseInt(match[1]);
              if (num > maxNumber) maxNumber = num;
            }
          }
        });
      }

      // 다음 번호로 코드 생성 (회사코드-BR번호 형식)
      const nextNumber = maxNumber + 1;
      return `${companyCode}-BR${String(nextNumber).padStart(3, '0')}`;
    } catch (error) {
      logger.error('Error generating branch code', error, { companyId });
      // 에러 시 타임스탬프 기반 코드 생성
      return `BR${Date.now().toString().slice(-4)}`;
    }
  }

  // 지점 생성
  static async createBranch(branchData: {
    branch_name: string;
    branch_code?: string;
    company_id: string;
    address?: string;
  }) {
    try {
      // 같은 회사 내에서 지점명 중복 체크
      const { data: existingBranch } = await supabase
        .from('branches')
        .select('id, branch_name')
        .eq('company_id', branchData.company_id)
        .eq('branch_name', branchData.branch_name)
        .single();

      if (existingBranch) {
        throw new Error(`이미 동일한 이름의 지점이 존재합니다: ${branchData.branch_name}`);
      }

      // branch_code가 없으면 자동 생성
      let branchCode = branchData.branch_code;
      if (!branchCode) {
        branchCode = await this.generateBranchCode(branchData.company_id);
      }

      const { data, error } = await supabase
        .from('branches')
        .insert({
          branch_name: branchData.branch_name,
          branch_code: branchCode,
          company_id: branchData.company_id,
          address: branchData.address
        })
        .select('*')
        .single();

      if (error) throw error;
      
      // company 정보를 별도로 조회
      if (data) {
        const { data: company } = await supabase
          .from('companies')
          .select('*')
          .eq('id', data.company_id)
          .single();
        
        return { ...data, company };
      }
      
      return data;
    } catch (error) {
      logger.error('Error creating branch', error, { branchData });
      throw error;
    }
  }

  // 지점 수정
  static async updateBranch(branchId: string, branchData: {
    branch_name?: string;
    branch_code?: string;
    address?: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('branches')
        .update(branchData)
        .eq('id', branchId)
        .select('*')
        .single();

      if (error) throw error;
      
      // company 정보를 별도로 조회
      if (data) {
        const { data: company } = await supabase
          .from('companies')
          .select('*')
          .eq('id', data.company_id)
          .single();
        
        return { ...data, company };
      }
      
      return data;
    } catch (error) {
      logger.error('Error updating branch', error, { id: branchId, branchData });
      throw error;
    }
  }

  // 지점 삭제
  static async deleteBranch(branchId: string) {
    try {
      // 지점에 속한 사용자가 있는지 확인
      const { count: userCount } = await supabase
        .from('users')
        .select('id', { count: 'exact' })
        .eq('branch_id', branchId)
        .eq('is_deleted', false);

      if ((userCount || 0) > 0) {
        throw new Error('지점에 속한 사용자가 있어 삭제할 수 없습니다.');
      }

      const { error } = await supabase
        .from('branches')
        .delete()
        .eq('id', branchId);

      if (error) throw error;
    } catch (error) {
      logger.error('Error deleting branch', error, { id: branchId });
      throw error;
    }
  }

  // 사용자를 다른 지점으로 이동
  static async moveUserToBranch(userId: string, newBranchId: string) {
    try {
      // 새 지점의 회사 ID 조회
      const { data: branch, error: branchError } = await supabase
        .from('branches')
        .select('company_id')
        .eq('id', newBranchId)
        .single();

      if (branchError) throw branchError;

      const { data, error } = await supabase
        .from('users')
        .update({
          branch_id: newBranchId,
          company_id: branch.company_id
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error moving user to branch', error, { userId, branchId: newBranchId });
      throw error;
    }
  }

  // 조직도 트리 데이터 조회
  static async getOrganizationTree() {
    try {
      const companies = await this.getCompanies();
      const branches = await this.getBranches();
      const users = await UserService.getUsers();

      // 사용자를 회사/지점별로 그룹화
      const usersByBranch: Record<string, User[]> = {};
      const usersByCompany: Record<string, User[]> = {};

      users.forEach(user => {
        if (user.is_active && !user.is_deleted) {
          if (user.branch_id) {
            if (!usersByBranch[user.branch_id]) {
              usersByBranch[user.branch_id] = [];
            }
            usersByBranch[user.branch_id].push(user);
          }
          if (user.company_id) {
            if (!usersByCompany[user.company_id]) {
              usersByCompany[user.company_id] = [];
            }
            usersByCompany[user.company_id].push(user);
          }
        }
      });

      return {
        companies,
        branches,
        usersByBranch,
        usersByCompany,
        totalUsers: users.filter(u => u.is_active && !u.is_deleted).length
      };
    } catch (error) {
      logger.error('Error fetching organization tree', error);
      throw error;
    }
  }
}